import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PairTabs, productTabs } from '../components/PairTabs'
import { PageHeader, PageBody, headerStyles } from '../components/PageHeader'
import pa from './ProductActivityPage.module.css'
import styles from './BatchesPage.module.css'
import { useAuth } from '../contexts/AuthContext'
import { apiJson } from '../lib/api'
import { useHelpPref } from '../lib/preferences'
import { useTasks } from '../components/TaskCentre'
import { SkeletonRows } from '../components/pending'
import { AsAt } from '../components/AsAt'
import { useRefetch } from '../lib/connection'
import { useLive } from '../lib/live'
import { todayLocal } from './timesheets/time'
import { rangeLabel } from './wages/format'
import { DatePeriod } from './productActivity/DatePeriod'
import { sharedPeriod, useSharedPeriod } from './productActivity/sharedPeriod'
import { datesLabel } from '../lib/keptDates'
import { StillLooking } from '../components/StillLooking'
import type { Cycle } from './productActivity/model'
import { BatchDetail } from './batches/BatchDetail'
import { SettingsModal } from './batches/SettingsModal'
import { workbook, XLSX_TYPE } from './batches/xlsx'
import {
  STATUS_CHIPS,
  batchSheet,
  cycleSheet,
  localLabel,
  matches,
  rowWeight,
  statusOf,
  totalLine,
  totalRow,
  warningChips,
  type Batch,
  type ListResponse,
  type StatusKey,
} from './batches/model'
import { openableRow } from '../lib/openableRow'
import { can, useAccess } from '../lib/access'
import { CONFLICTS_PATH, WeighingConflicts } from './batches/WeighingConflicts'

// Batches (CONTROL V4 "Batches", handoff §7k): what travels together in one
// bin. Batches are opened by the field station, never here; this screen
// watches them, corrects them through audited actions and exports them.

const HELP: [string, string][] = [
  [
    'A batch is what travels together',
    'Normally the buckets tipped or placed into one bin. The batch carries the product cycles inside it, so everything about where the fruit came from and who picked it moves with the bin as one thing.',
  ],
  [
    'Batches are made in the field, not here',
    'The field station opens a batch when the first bucket is scanned into it. Nobody creates one by hand in CONTROL. This screen is for watching them, correcting them and printing their labels.',
  ],
  [
    'What a batch label carries',
    'The identity of the batch and whatever the cog is set to include, which is why the settings matter more than they look. A packhouse sorting by text on the label depends on that text being there.',
  ],
  [
    'Where it goes wrong',
    'A bin that was never closed, cycles that arrived after the batch was sealed, or two batches opened for one bin. Those show as problems on the row rather than being silently merged, because merging the wrong two bins is not recoverable.',
  ],
  [
    'Traceability is the point',
    'Everything downstream reads the batch: transfers, quality deductions and anything a customer or auditor asks for later. Correct it here and the whole chain follows.',
  ],
]

const COLUMNS = ['Batch', 'Status', 'Bin', 'Product', 'Picking job', 'Grower / orchard', 'Items', 'Weight', 'Last activity', 'Warnings']

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

export function BatchesPage() {
  const { user } = useAuth()
  const today = todayLocal()
  // Product activity links here with ?from, ?to, ?q and ?batch (§7k.5).
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const linked = (k: string) => {
    const v = params.get(k)
    return v && DATE_RE.test(v) ? v : null
  }
  // Otherwise Product Activity's period, shared with Reports (§7l.3).
  const [from, setFrom] = useState(() => linked('from') || sharedPeriod(today, user?.uid).from)
  const [to, setTo] = useState(() => linked('to') || linked('from') || sharedPeriod(today, user?.uid).to)
  const kept = useSharedPeriod(user?.uid, today, from, to)
  const [data, setData] = useState<ListResponse | null>(null)
  // The period the rows shown were loaded for. Until it is the period chosen
  // the table body is skeleton rows (h 3994-4002); a reload of the same period
  // after a save keeps the rows up.
  const [shown, setShown] = useState<string | null>(null)
  const [asAt, setAsAt] = useState<Date | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState('')
  const [query, setQuery] = useState(() => params.get('q') || '')
  const [status, setStatus] = useState<StatusKey>('all')
  const [openId, setOpenId] = useState<string | null>(() => params.get('batch'))
  const [cog, setCog] = useState(false)
  // Weighing conflicts (D7.8): a link from Late data opens straight on one (?conflict=).
  const access = useAccess()
  const [conflictId, setConflictId] = useState<string | null>(() => params.get('conflict'))
  const [conflictsOpen, setConflictsOpen] = useState(() => !!params.get('conflict'))
  const [conflictCount, setConflictCount] = useState<number | null>(null)
  const [help, setHelp] = useHelpPref(user, 'batches')
  const { startTask } = useTasks()

  const userRef = useRef(user)
  userRef.current = user
  const uid = user?.uid
  const seq = useRef(0)
  // The period the rows were loaded for: a refetch of it that fails keeps the rows up and says so; another period's failure shows none of the old.
  const dataKey = useRef<string | null>(null)
  const load = useCallback(async () => {
    const u = userRef.current
    if (!u) return
    const n = ++seq.current
    setError(null)
    try {
      const r = await apiJson<ListResponse>(u, `/api/batches?start_date=${from}&end_date=${to}`)
      if (n !== seq.current) return
      dataKey.current = `${from}_${to}`
      setData(r)
      setAsAt(new Date())
    } catch (e: any) {
      if (n !== seq.current) return
      if (dataKey.current !== `${from}_${to}`) {
        dataKey.current = null
        setData(null)
      }
      setError(e?.message || 'Could not load batches')
    } finally {
      if (n === seq.current) setShown(`${from}_${to}`)
    }
  }, [uid, from, to])
  const loading = shown !== `${from}_${to}`
  // How many held weighings wait. Not part of the period: a conflict is open until it is decided, whatever its day.
  const loadConflicts = useCallback(async () => {
    const u = userRef.current
    if (!u) return
    try {
      const r = await apiJson<{ items: unknown[] }>(u, CONFLICTS_PATH)
      setConflictCount(r.items.length)
    } catch {
      setConflictCount(null)
    }
  }, [uid])
  useEffect(() => {
    loadConflicts()
  }, [loadConflicts])

  useEffect(() => {
    load()
  }, [load])
  // h 4167-4171: it fetches again on window focus and on reconnect, and a
  // period that includes today is pushed (a batch's figures are its cycles').
  useRefetch(load)
  const includesToday = from <= today && to >= today
  const live = useLive(user, ['batches', 'cycles'], includesToday ? (d) => d >= from && d <= to : null, load)

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 3400)
    return () => clearTimeout(t)
  }, [toast])

  const batches = data?.batches || []
  const rows = batches.filter((b) => matches(b, status, query))

  // A background task (§10.4): the rows shown now are what it exports, and
  // the file finishes into the task centre.
  const exportXlsx = () => {
    const shown = rows
    const name = `batches-${from}_${to}.xlsx`
    startTask({
      label: 'Export batches',
      note: `${shown.length} batch${shown.length === 1 ? '' : 'es'} · ${rangeLabel(from, to)}`,
      file: name,
      run: async () => {
        const r = await apiJson<{ cycles: Cycle[] }>(user, '/api/batches/members', { body: { batch_ids: shown.map((b) => b.id) } })
        const codes = Object.fromEntries(shown.map((b) => [b.id, b.batch_code]))
        const bytes = workbook([
          { name: 'Batches', rows: batchSheet(shown) },
          { name: 'Cycles', rows: cycleSheet(r.cycles, codes) },
        ])
        return {
          file: { name, content: bytes, type: XLSX_TYPE },
          note: `${shown.length} batch${shown.length === 1 ? '' : 'es'} and ${r.cycles.length} member cycle${r.cycles.length === 1 ? '' : 's'}: two sheets`,
        }
      },
    })
  }

  const openCycle = (c: Cycle) => navigate(`/product-activity?from=${c.work_date}&to=${c.work_date}&cycle=${encodeURIComponent(c.id)}`)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <StillLooking at={kept.at} onDefaults={from === today && to === today} label={datesLabel(from, to, today)} onContinue={kept.confirm} onDefault={() => { setFrom(today); setTo(today) }} />
      <PageHeader
        title="Batches"
        tabs={<PairTabs label="Product activity and batches" tabs={productTabs(from, to)} current="Batches" />}
        actions={
          <>
            <button type="button" className={headerStyles.btn} onClick={() => setConflictsOpen(true)} data-testid="conflicts-open">
              Weighing conflicts{conflictCount ? ` (${conflictCount})` : ''}
            </button>
            <button type="button" className={headerStyles.btnPrimary} onClick={exportXlsx} disabled={loading || !rows.length}>
              <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Export current results
            </button>
            <button
              type="button"
              className={headerStyles.btnHelp}
              onClick={() => {
                setHelp(!help)
              }}
            >
              {help ? 'Hide help' : 'How batches work'}
            </button>
            <button type="button" className={headerStyles.iconBtn} title="Batch settings for this subscription" aria-label="Batch settings for this subscription" onClick={() => setCog(true)} disabled={!data}>
              <Gear />
            </button>
          </>
        }
      />

      <PageBody className={styles.body}>
        {help && (
          <div className={[pa.helpGrid, styles.help].join(' ')}>
            <button
              type="button"
              className={pa.helpClose}
              onClick={() => setHelp(false)}
              aria-label="Close help"
              title="Close help"
            >
              ×
            </button>
            {HELP.map(([h, body]) => (
              <div key={h} className={pa.helpCard}>
                <span>{h}</span>
                <p>{body}</p>
              </div>
            ))}
          </div>
        )}

        <div className={[pa.toolbar, styles.toolbar].join(' ')}>
          <DatePeriod
            user={user}
            from={from}
            to={to}
            onApply={(f, t) => {
              setFrom(f)
              setTo(t)
              setOpenId(null)
            }}
          />
          {/* The count is of the rows shown, so it waits with them. */}
          {!loading && (
            <div className={styles.toolbarCount}>
              <strong data-testid="summary">
                {rows.length} batch{rows.length === 1 ? '' : 'es'}
              </strong>
              <span className={pa.totals}>{totalLine(rows)}</span>
            </div>
          )}
          {!loading && <AsAt at={asAt} live={live === 'live'} className={pa.asAt} />}
        </div>

        <div className={[pa.filters, styles.filters].join(' ')}>
          <div className={pa.searchWrap}>
            <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={pa.searchIcon}>
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input className={pa.search} placeholder="Batch, bin, product, job…" aria-label="Search batches" value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
          {STATUS_CHIPS.map((c) => (
            <button key={c.key} type="button" aria-pressed={status === c.key} className={status === c.key ? styles.chipOn : styles.chip} onClick={() => setStatus(c.key)}>
              {c.label}{loading ? '' : ' '}
              {!loading && <span className={styles.chipCount}>{batches.filter(c.count).length}</span>}
            </button>
          ))}
          <span className={styles.filterNote}>
            Weight is what is in the bin. The weighed figure where there is one, otherwise the gross sum from Product activity. With the credited weight beneath it when
            they differ
          </span>
        </div>

        {error && (
          <div className={pa.errorBar} role="alert">
            <span>{data ? `Batches could not be refreshed: ${error}. What is shown may be out of date.` : error}</span>
            <button type="button" className={pa.flashX} onClick={() => load()}>
              Retry
            </button>
          </div>
        )}

        <div className={[pa.card, styles.tableCard].join(' ')}>
          {!loading && rows.length === 0 ? (
            <div className={pa.empty}>{error && !data ? 'Batches could not be loaded.' : 'No batches match these filters.'}</div>
          ) : (
            <div className={pa.table} role="table" aria-label="Batches">
              <div className={styles.headRow} role="row">
                {COLUMNS.map((h) => (
                  <span key={h} role="columnheader">
                    {h}
                  </span>
                ))}
              </div>
              {loading ? (
                <SkeletonRows rowClass={styles.row} cols={COLUMNS.length} label="Loading batches" />
              ) : (
                <>
                  {rows.map((b) => <BatchRow key={b.id} b={b} onOpen={() => setOpenId(b.id)} />)}
                  <TotalRow rows={rows} />
                </>
              )}
            </div>
          )}
        </div>

        {openId && (
          <BatchDetail
            key={openId}
            user={user}
            batchId={openId}
            onClose={() => setOpenId(null)}
            onChanged={load}
            toast={setToast}
            onOpenBatch={setOpenId}
            onViewBin={(bin) => {
              setQuery(bin)
              setStatus('all')
            }}
            onOpenCycle={openCycle}
          />
        )}
        {conflictsOpen && (
          <WeighingConflicts
            user={user}
            canEdit={can(access, 'batches', 'edit')}
            initialId={conflictId}
            onClose={() => {
              setConflictsOpen(false)
              setConflictId(null)
              loadConflicts()
            }}
            onChanged={() => {
              load()
              loadConflicts()
            }}
            toast={setToast}
          />
        )}
        {cog && data && (
          <SettingsModal
            user={user}
            settings={data.settings}
            batches={batches}
            onClose={() => setCog(false)}
            onSaved={(msg) => {
              setCog(false)
              setToast(msg)
              load()
            }}
          />
        )}
        {toast && (
          <div className={pa.toast} role="status">
            {toast}
          </div>
        )}
      </PageBody>
    </div>
  )
}

// The V4 cog (Feather "settings"), the same glyph as the other screens' cogs.
function Gear() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6 1.65 1.65 0 0 0 10 3.09V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
    </svg>
  )
}

// The foot row: the batches in view, so it follows the search and status chip.
function TotalRow({ rows }: { rows: Batch[] }) {
  const t = totalRow(rows)
  return (
    <div className={styles.totalRow} role="row" data-testid="batch-total">
      <span className={styles.totalLabel}>Total · {rows.length} batch{rows.length === 1 ? '' : 'es'}</span>
      <span />
      <span />
      <span />
      <span />
      <span />
      <span className={pa.num}>{t.items}</span>
      <span className={styles.cellStack}>
        <span className={styles.kg}>{t.kg}</span>
        {t.sub && <span className={styles.sub}>{t.sub}</span>}
      </span>
      <span />
      <span />
    </div>
  )
}

function BatchRow({ b, onOpen }: { b: Batch; onOpen: () => void }) {
  const st = statusOf(b)
  const w = rowWeight(b)
  const last = b.last_local
  return (
    <div className={[styles.row, b.excluded ? styles.rowExcluded : ''].join(' ')} role="row" data-testid="batch-row" {...openableRow(onOpen)}>
      <span className={[styles.code, styles.codeAccent].join(' ')} title={b.batch_code}>{b.batch_code}</span>
      <span className={styles.state} style={{ background: st.bg, color: st.fg }}>
        {st.label}
      </span>
      <span className={styles.bin}>{b.bin_code || '—'}</span>
      <span className={pa.cellMuted}>{b.product || '—'}</span>
      <span className={pa.cellMuted}>{b.picking_job_name || '—'}</span>
      <span className={styles.cellStack}>
        <span className={styles.cellText}>{b.site_name || '—'}</span>
        {b.blocks.length > 0 && <span className={styles.sub}>{b.blocks.join(', ')}</span>}
      </span>
      <span className={pa.num}>{b.items}</span>
      <span className={styles.cellStack}>
        <span className={styles.kg}>{w.kg}</span>
        {w.sub && <span className={styles.sub}>{w.sub}</span>}
      </span>
      <span className={pa.cellMuted}>{last ? localLabel(last) : '—'}</span>
      <div className={pa.chips}>
        {warningChips(b).map((c) => (
          <span key={c.label} className={pa.chip} style={{ background: c.bg, color: c.fg }}>
            {c.label}
          </span>
        ))}
      </div>
    </div>
  )
}
